<template>
  <div class="rental-task-detail">
    <el-form
      ref="formRef"
      :model="state.taskInfo"
      :rules="rules"
      label-width="132px"
      size="default"
      status-icon
      require-asterisk-position="right"
      label-position="left"
      :disabled="disabled"
    >

      <!-- Site Selection -->
      <el-form-item label="Site" prop="siteProjectId">
        <DropdownSelect
          v-model="state.taskInfo.siteProjectId"
          :options="state.dropdownSite"
          value-key="siteProjectId"
          label-key="siteName"
          description-key="siteProjectId"
          placeholder="Select Site"
          :loading="state.loadingSite"
          :remote="true"
          :remote-method="searchSites"
          :show-description="true"
          @change="setSite"
          @visible-change="(v: boolean) => v && loadSitesFromApi()"
        />
      </el-form-item>

      <!-- Overhead Toggle -->
      <el-form-item label="Overhead" prop="overHead">
        <div class="flex justify-end w-[400px]">
          <el-switch
            v-model="state.taskInfo.overHead"
            size="large"
            @change="(val: any) => handleOverheadChange(!!val)"
          />
        </div>
      </el-form-item>

      <!-- Fleet Selection (hidden when overhead=true) -->
      <el-form-item v-if="!state.taskInfo.overHead" label="Fleet" prop="assetProjectId">
        <DropdownSelect
          v-model="state.taskInfo.assetProjectId"
          :options="state.dropdownFleet"
          value-key="assetProjectId"
          label-key="assetName"
          description-key="assetProjectId"
          placeholder="Select Fleet"
          :loading="state.loadingFleet"
          :disabled="state.taskInfo.overHead"
          :remote="true"
          :show-description="true"
          :remote-method="searchEquipments"
          @change="setFleet"
          @visible-change="(v: boolean) => v && loadEquipmentsFromApi(state.taskInfo.siteProjectId)"
        />
      </el-form-item>

      <!-- Work Order Selection (hidden when overhead=true) -->
      <el-form-item v-if="!state.taskInfo.overHead" label="Work Order" prop="workOrderProjectId">
        <DropdownSelect
          v-model="state.taskInfo.workOrderProjectId"
          :options="state.dropdownWorkOrder"
          value-key="workOrderProjectId"
          label-key="workOrderName"
          description-key="description"
          placeholder="Select Work Order"
          :loading="state.loadingWorkOrder"
          :disabled="state.taskInfo.overHead || (!!state.taskInfo.siteProjectId && !state.taskInfo.assetProjectId)"
          :remote="true"
          :remote-method="searchWorkOrders"
          @change="setWorkOrder"
          @visible-change="(v: boolean) => v && loadWorkOrdersFromApi()"
        />
      </el-form-item>

      <!-- Task Selection (only shown when overhead=true) -->
      <el-form-item v-if="state.taskInfo.overHead" label="Task" prop="npCode">
        <DropdownSelect
          v-model="state.taskInfo.npCode"
          :options="state.dropdownWorkOrderTask"
          value-key="npCode"
          label-key="npCodeName"
          description-key="description"
          placeholder="Select Task"
          :loading="state.loadingWorkOrderTask"
          :disabled="!state.taskInfo.overHead"
          :remote="true"
          :remote-method="searchTasks"
          @change="setTask"
          @visible-change="(v: boolean) => v && loadTasksFromApi()"
        />
      </el-form-item>

      <!-- Job Description -->
      <el-form-item label="Job Description" prop="comments">
        <el-input
          v-model="state.taskInfo.comments"
          :rows="2"
          type="textarea"
          class="w-[400px] job-description"
          placeholder="Enter Job Description"
          size="large"
          @input="setJobDescription"
        />
      </el-form-item>

      <!-- Field Service Section -->
      <div v-if="allowInputFieldService">
        <!-- Field Service Toggle -->
        <el-form-item label="Field Service" prop="additionInfo">
          <div class="flex justify-end w-[400px]">
             <el-switch
               v-model="state.taskInfo.additionInfo"
               size="large"
               @change="(val: any) => {
                 setFieldService(!!val);
                 // Clear validation errors when toggling field service
                 if (formRef.value) {
                   formRef.value.clearValidate();
                 }
               }"
             />
          </div>
        </el-form-item>

        <!-- Field Service Fields - using shared component -->
        <FieldServiceForm
          v-if="state.taskInfo.additionInfo"
          ref="fieldServiceFormRef"
          :model-value="state.taskInfo"
          :sig-option="sigOption"
          :disabled="props.disabled"
          @update:field="handleFieldServiceUpdate"
        />
      </div>
    </el-form>
  </div>

  <!-- Confirm Dialog -->
  <ConfirmDialog
    v-model:visible="showOverheadConfirm"
    title="Switching to Overhead"
    message="All current task data will be cleared.&#10;Do you want to continue?"
    lblBtnSubmit="Continue"
    lblBtnCancel="Cancel"
    iconBtnName="check-mark-circle"
    iconPopup="warring"
    :width="400"
    @submit="handleOverheadConfirm"
    @cancel="handleOverheadCancel"
  />
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import useRentalTaskDetail from "@/composables/v2/useRentalTaskDetail";
import ConfirmDialog from "@/components/v2/ConfirmDialog.vue";
import FieldServiceForm from "@/components/v2/FieldServiceForm.vue";
import DropdownSelect from "@/components/v2/DropdownSelect.vue";
import type { FieldServiceInfo } from "@/composables/v2/useFieldService";
import { useOverheadConfirmation } from "@/composables/v2/useOverheadConfirmation";
import { useFormHelpers } from "@/composables/v2/useFormHelpers";
import { COMPANY_IDS } from "@/composables/v2/types";

const props = defineProps<{ 
  taskId: string;
  initialData?: any;
  isEditMode?: boolean;
  disabled?: boolean;
}>();

const {
  state,
  rules,
  allowInputFieldService,
  sigOption,
  loadSitesFromApi,
  searchSites,
  loadEquipmentsFromApi,
  searchEquipments,
  loadTasksFromApi,
  searchTasks,
  loadWorkOrdersFromApi,
  searchWorkOrders,
  setOverhead,
  setSite,
  setFleet,
  setWorkOrder,
  setTask,
  setJobDescription,
  setFieldService,
  uploadSignatures,
  // Inject functions for edit mode
  injectSiteIfNeeded,
  injectAssetIfNeeded,
  injectWorkOrderIfNeeded,
} = useRentalTaskDetail();

// Ref to FieldServiceForm component for signature access
const fieldServiceFormRef = ref();

// Load initial data for edit mode
const loadInitialData = async (data: any) => {
  if (!data) return;
  
  // Map API response fields to state (handle both PascalCase and camelCase)
  state.taskInfo.siteProjectId = data.SiteProjectID || data.siteProjectId || undefined;
  state.taskInfo.siteName = data.SiteName || data.siteName || undefined;
  state.taskInfo.assetProjectId = data.AssetProjectID || data.assetProjectId || undefined;
  state.taskInfo.assetName = data.AssetName || data.assetName || undefined;
  state.taskInfo.workOrderProjectId = data.WorkOrderProjectID || data.workOrderProjectId || undefined;
  state.taskInfo.workOrderName = data.WorkOrderName || data.workOrderName || undefined;
  state.taskInfo.npCode = data.NPCode || data.npCode || undefined;
  state.taskInfo.comments = data.Comments || data.comments || '';
  state.taskInfo.overHead = data.OverHead ?? data.overHead ?? false;
  state.taskInfo.additionInfo = data.FieldService ?? data.fieldService ?? false;
  state.taskInfo.companyId = data.CompanyID || data.companyId || undefined;
  
  // Map additional info from TimesheetAdditionals
  const additional = data.TimesheetAdditionals?.[0] || data.timesheetAdditionals?.[0];
  if (additional) {
    state.taskInfo.manufacturer = additional.Manufacturer || additional.manufacturer || '';
    state.taskInfo.model = additional.Model || additional.model || '';
    state.taskInfo.SMUHour = additional.Smureading || additional.smureading || 0;
    state.taskInfo.serialNo = additional.SerialNumber || additional.serialNumber || '';
    state.taskInfo.clientName = additional.CustomerName || additional.customerName || '';
    state.taskInfo.customerEmail = additional.CustomerEmail || additional.customerEmail || '';
    state.taskInfo.location = additional.Locations || additional.locations || '';
    state.taskInfo.travelKMS = additional.TravelKms || additional.travelKms || 0;
    state.taskInfo.vehRegNo = additional.Rego || additional.rego || '';
    state.taskInfo.mechanicalAcceptance = additional.WorkerSignature || additional.workerSignature || '';
    state.taskInfo.customerAcceptance = additional.CustomerSignature || additional.customerSignature || '';
    state.taskInfo.fieldServiceNote = additional.AdditionalInfo || additional.additionalInfo || '';
    state.taskInfo.customerAcceptanceName = additional.CustomerAcceptanceName || additional.customerAcceptanceName || '';
    
    // Set validation flags for existing signatures so validation passes
    // 'existing' indicates the signature was loaded from server (not drawn)
    if (state.taskInfo.customerAcceptance) {
      state.taskInfo.isCustomerAcceptance = 'existing';
    }
    if (state.taskInfo.mechanicalAcceptance) {
      state.taskInfo.isMechanicalAcceptance = 'existing';
    }
  }
  
  // Preload dropdown data for existing values
  await preloadDropdowns(data);
};

// Preload dropdown data based on existing values
const preloadDropdowns = async (data: any) => {
  const siteId = data.SiteProjectID || data.siteProjectId;
  const assetId = data.AssetProjectID || data.assetProjectId;
  const workOrderId = data.WorkOrderProjectID || data.workOrderProjectId;
  const isOverhead = data.OverHead ?? data.overHead ?? false;
  
  try {
    // 1. Inject specific items first to ensure we have the real names
    if (siteId) {
      const injectedSite = await injectSiteIfNeeded(siteId);
      if (injectedSite) {
        state.taskInfo.siteName = injectedSite.siteName;
      }
    }
    
    if (assetId) {
      const injectedAsset = await injectAssetIfNeeded(siteId, assetId);
      if (injectedAsset) {
        state.taskInfo.assetName = injectedAsset.assetName;
      }
    }
    
    if (workOrderId) {
      const injectedWorkOrder = await injectWorkOrderIfNeeded(assetId, workOrderId);
      if (injectedWorkOrder) {
        state.taskInfo.workOrderName = injectedWorkOrder.workOrderName;
      }
    }

    // 2. Load general dropdown lists
    await loadSitesFromApi();
    
    if (siteId) {
      await loadEquipmentsFromApi(siteId);
    }
    
    if (assetId) {
      await loadWorkOrdersFromApi();
    }
    
    if (isOverhead) {
      await loadTasksFromApi();
    }
  } catch (err) {
    // Error loading dropdowns
  }
};

const loadedDataId = ref<string | null>(null);
watch(() => props.initialData, (newData) => {
  // For copied data, use a unique identifier to ensure it always loads
  const isCopy = newData?.isCopy ?? false;
  const dataId = isCopy 
    ? `copy-${Date.now()}-${JSON.stringify(newData?.SiteProjectID)}`
    : (newData?.TimesheetLineId || newData?.id || JSON.stringify(newData?.SiteProjectID));
  
  if (loadedDataId.value === dataId && dataId && !isCopy) {
    return;
  }
  
  if (newData) {
    loadedDataId.value = dataId;
    loadInitialData(newData);
  }
}, { immediate: true });

// Handler for FieldServiceForm updates
const handleFieldServiceUpdate = (field: keyof FieldServiceInfo, value: any) => {
  (state.taskInfo as any)[field] = value;
};

const formRef = ref();

// Use shared overhead confirmation composable
const resetRentalSpecificFields = () => {
  // siteProjectId and siteName are kept as they are common for both modes (matching ForceTaskDetail)
  state.taskInfo.assetProjectId = undefined;
  state.taskInfo.assetName = undefined;
  state.taskInfo.workOrderProjectId = undefined;
  state.taskInfo.workOrderName = undefined;
  state.taskInfo.npCode = undefined;
  state.taskInfo.npCodeName = undefined;
  state.taskInfo.comments = "";
};

const {
  showOverheadConfirm,
  handleOverheadChange,
  handleOverheadConfirm,
  handleOverheadCancel,
} = useOverheadConfirmation(state, setOverhead, resetRentalSpecificFields);

// Use shared form helpers
const { hasData } = useFormHelpers(formRef, state);

// Get task data for form submission
const getTaskData = () => {
  const taskInfo = state.taskInfo;
  
  let timesheetAdditional = null;
  if (taskInfo.additionInfo) {
    timesheetAdditional = {
      manufacturer: taskInfo.manufacturer || '',
      model: taskInfo.model || '',
      smureading: taskInfo.SMUHour || 0,
      serialNumber: taskInfo.serialNo || '',
      customerName: taskInfo.clientName || '',
      customerEmail: taskInfo.customerEmail || '',
      locations: taskInfo.location || '',
      travelKms: taskInfo.travelKMS || 0,
      rego: taskInfo.vehRegNo || '',
      workerSignature: taskInfo.mechanicalAcceptance || '',
      customerSignature: taskInfo.customerAcceptance || '',
      additionalInfo: taskInfo.fieldServiceNote || '',
      customerAcceptanceName: taskInfo.customerAcceptanceName || ''
    };
  }

  return {
    comments: taskInfo.comments || '',
    overHead: taskInfo.overHead || false,
    companyId: taskInfo.companyId || COMPANY_IDS.RENTAL,
    npCode: taskInfo.overHead ? (taskInfo.npCode || null) : null,
    fieldService: taskInfo.additionInfo || false,
    assetProjectId: taskInfo.assetProjectId || null,
    siteProjectId: taskInfo.siteProjectId || null,
    workOrderProjectId: taskInfo.workOrderProjectId || null,
    timesheetAdditional: timesheetAdditional
  };
};

// Get task data with uploaded signatures (call this before submit)
const getTaskDataWithSignatures = async () => {
  // Upload signatures if field service is enabled and signatures were drawn
  if (state.taskInfo.additionInfo) {
    const customerRef = fieldServiceFormRef.value?.signatureCustomer;
    const mechanicalRef = fieldServiceFormRef.value?.signatureMechanical;
    await uploadSignatures(customerRef, mechanicalRef);
  }
  return getTaskData();
};

// Expose for parent
defineExpose({
  formRef,
  taskId: props.taskId,
  hasData,
  getTaskData,
  getTaskDataWithSignatures,
  loadInitialData,
  fieldServiceFormRef
});
</script>

<style lang="scss">
.rental-task-detail {
  .customer-acceptance-name {
    label {
      line-height: 20px !important;
    }
  }

  .customer-signature {
    .el-form-item__label {
      line-height: 20px !important;
    }
  }

  .mechanical-signature {
    .el-form-item__label {
      line-height: 20px !important;
    }
  }

  .sign-pad {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
  }

  .is-error {
    .sign-pad {
      border-color: #f56c6c !important;
    }
  }

  .job-description {
    textarea {
      min-height: 100px !important;
    }
  }
}
</style>
